import { useState, useCallback } from 'react';
import { FiPlus, FiTrash2, FiAlertTriangle, FiInfo, FiCopy, FiChevronDown } from 'react-icons/fi';
import { getCommonHeaderTemplates, validateCustomHeaders } from '../../api/monitoring';
import styles from './CustomHeadersEditor.module.css';

interface CustomHeadersEditorProps {
  headers: Record<string, string>;
  onChange: (headers: Record<string, string>) => void;
  disabled?: boolean;
  showTemplates?: boolean;
  className?: string;
}

export default function CustomHeadersEditor({
  headers,
  onChange,
  disabled = false,
  showTemplates = true,
  className
}: CustomHeadersEditorProps) {
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [newHeaderKey, setNewHeaderKey] = useState('');
  const [newHeaderValue, setNewHeaderValue] = useState('');
  
  const headerTemplates = getCommonHeaderTemplates();
  const validation = validateCustomHeaders(headers);

  const handleAddHeader = useCallback(() => {
    if (!newHeaderKey.trim() || !newHeaderValue.trim()) return;
    
    const updatedHeaders = {
      ...headers,
      [newHeaderKey.trim()]: newHeaderValue.trim()
    };
    
    onChange(updatedHeaders);
    setNewHeaderKey('');
    setNewHeaderValue('');
  }, [headers, newHeaderKey, newHeaderValue, onChange]);

  const handleUpdateHeader = useCallback((oldKey: string, newKey: string, newValue: string) => {
    const updatedHeaders = { ...headers };
    
    // Remove old key if key changed
    if (oldKey !== newKey) {
      delete updatedHeaders[oldKey];
    }
    
    // Set new key/value
    updatedHeaders[newKey] = newValue;
    
    onChange(updatedHeaders);
  }, [headers, onChange]);

  const handleRemoveHeader = useCallback((key: string) => {
    const updatedHeaders = { ...headers };
    delete updatedHeaders[key];
    onChange(updatedHeaders);
  }, [headers, onChange]);

  const handleApplyTemplate = useCallback((templateName: string) => {
    const template = headerTemplates[templateName];
    if (template) {
      onChange({ ...headers, ...template });
    }
    setShowTemplateDropdown(false);
  }, [headers, onChange, headerTemplates]);

  const handleClearAll = useCallback(() => {
    onChange({});
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddHeader();
    }
  }, [handleAddHeader]);

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>Custom Headers</h3>
        <div className={styles.headerActions}>
          {Object.keys(headers).length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className={styles.clearButton}
              disabled={disabled}
              title="Clear all headers"
            >
              Clear All
            </button>
          )}
          {showTemplates && (
            <div className={styles.templateDropdown}>
              <button
                type="button"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                className={styles.templateButton}
                disabled={disabled}
              >
                <FiCopy size={14} />
                Templates
                <FiChevronDown size={12} />
              </button>
              {showTemplateDropdown && (
                <div className={styles.templateMenu}>
                  {Object.keys(headerTemplates).map(templateName => (
                    <button
                      key={templateName}
                      type="button"
                      onClick={() => handleApplyTemplate(templateName)}
                      className={styles.templateItem}
                    >
                      {templateName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Security Warning */}
      <div className={styles.securityWarning}>
        <FiAlertTriangle className={styles.warningIcon} size={16} />
        <div className={styles.warningContent}>
          <strong>Security Notice:</strong> Headers are stored unencrypted in the database. 
          Avoid storing sensitive authentication tokens or personal data.
        </div>
      </div>

      {/* Validation Messages */}
      {(validation.warnings.length > 0 || validation.errors.length > 0) && (
        <div className={styles.validation}>
          {validation.errors.map((error, index) => (
            <div key={`error-${index}`} className={styles.validationError}>
              <FiAlertTriangle size={14} />
              {error}
            </div>
          ))}
          {validation.warnings.map((warning, index) => (
            <div key={`warning-${index}`} className={styles.validationWarning}>
              <FiInfo size={14} />
              {warning}
            </div>
          ))}
        </div>
      )}

      {/* Existing Headers */}
      <div className={styles.headersList}>
        {Object.entries(headers).map(([key, value]) => (
          <HeaderRow
            key={key}
            headerKey={key}
            headerValue={value}
            onUpdate={(newKey, newValue) => handleUpdateHeader(key, newKey, newValue)}
            onRemove={() => handleRemoveHeader(key)}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Add New Header */}
      <div className={styles.addHeader}>
        <div className={styles.addHeaderInputs}>
          <input
            type="text"
            placeholder="Header name (e.g., User-Agent)"
            value={newHeaderKey}
            onChange={(e) => setNewHeaderKey(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.headerKeyInput}
            disabled={disabled}
          />
          <input
            type="text"
            placeholder="Header value"
            value={newHeaderValue}
            onChange={(e) => setNewHeaderValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.headerValueInput}
            disabled={disabled}
          />
          <button
            type="button"
            onClick={handleAddHeader}
            disabled={disabled || !newHeaderKey.trim() || !newHeaderValue.trim()}
            className={styles.addButton}
            title="Add header"
          >
            <FiPlus size={16} />
          </button>
        </div>
      </div>

      {/* Help Text */}
      <div className={styles.helpText}>
        <FiInfo size={14} />
        <span>
          Custom headers help avoid bot detection by making requests appear more like real browsers. 
          Use browser templates for common configurations.
        </span>
      </div>
    </div>
  );
}

interface HeaderRowProps {
  headerKey: string;
  headerValue: string;
  onUpdate: (key: string, value: string) => void;
  onRemove: () => void;
  disabled: boolean;
}

function HeaderRow({ headerKey, headerValue, onUpdate, onRemove, disabled }: HeaderRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editKey, setEditKey] = useState(headerKey);
  const [editValue, setEditValue] = useState(headerValue);

  const handleSave = useCallback(() => {
    if (editKey.trim() && editValue.trim()) {
      onUpdate(editKey.trim(), editValue.trim());
      setIsEditing(false);
    }
  }, [editKey, editValue, onUpdate]);

  const handleCancel = useCallback(() => {
    setEditKey(headerKey);
    setEditValue(headerValue);
    setIsEditing(false);
  }, [headerKey, headerValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  if (isEditing) {
    return (
      <div className={styles.headerRow}>
        <input
          type="text"
          value={editKey}
          onChange={(e) => setEditKey(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.headerKeyInput}
          disabled={disabled}
          autoFocus
        />
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.headerValueInput}
          disabled={disabled}
        />
        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={handleSave}
            className={styles.saveButton}
            disabled={disabled || !editKey.trim() || !editValue.trim()}
            title="Save changes"
          >
            ✓
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className={styles.cancelButton}
            disabled={disabled}
            title="Cancel changes"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.headerRow} onDoubleClick={() => !disabled && setIsEditing(true)}>
      <div className={styles.headerKey} title={headerKey}>{headerKey}</div>
      <div className={styles.headerValue} title={headerValue}>{headerValue}</div>
      <div className={styles.headerActions}>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className={styles.editButton}
          disabled={disabled}
          title="Edit header"
        >
          ✏️
        </button>
        <button
          type="button"
          onClick={onRemove}
          className={styles.removeButton}
          disabled={disabled}
          title="Remove header"
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  );
}
